import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSetupStatus, registerFirstOrg, testSrApiKey, saveSrApiKey } from '../../api/client';

type Step = 1 | 2 | 3 | 4;

export default function Setup() {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>(1);
  const [orgName, setOrgName] = useState('');
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [srApiKey, setSrApiKey] = useState('');
  const [srTestStatus, setSrTestStatus] = useState<'idle' | 'testing' | 'valid' | 'invalid'>('idle');
  const [srTestError, setSrTestError] = useState('');
  const [checkingFirstRun, setCheckingFirstRun] = useState(true);

  useEffect(() => {
    getSetupStatus()
      .then(({ firstRun }) => {
        if (!firstRun) {
          navigate('/login', { replace: true });
          // Stay in loading state while redirect happens — don't flip checkingFirstRun
        } else {
          setCheckingFirstRun(false);
        }
      })
      .catch(() => {
        // Network error — let user proceed with the wizard
        setCheckingFirstRun(false);
      });
  }, [navigate]);

  async function handleRegister(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await registerFirstOrg(orgName.trim(), email.trim().toLowerCase());
      setStep(2);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Something went wrong. Please try again.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  }

  async function handleTestSrKey() {
    if (!srApiKey.trim()) return;
    setSrTestStatus('testing');
    setSrTestError('');
    try {
      const result = await testSrApiKey(srApiKey.trim());
      setSrTestStatus(result.valid ? 'valid' : 'invalid');
      if (!result.valid) setSrTestError(result.error ?? 'API key is invalid');
    } catch {
      setSrTestStatus('invalid');
      setSrTestError('Could not reach the server. Please try again.');
    }
  }

  async function handleSaveSrKey() {
    setError('');
    setLoading(true);
    try {
      await saveSrApiKey(srApiKey.trim());
      setStep(4);
    } catch {
      setError('Failed to save API key. You can add it later in Settings.');
      setStep(4);
    } finally {
      setLoading(false);
    }
  }

  if (checkingFirstRun) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950">
        <div className="text-gray-500 dark:text-gray-400">Loading…</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 dark:bg-gray-950 px-4">
      <div className="w-full max-w-md">
        {/* Progress dots */}
        <div className="flex gap-2 justify-center mb-8">
          {([1, 2, 3, 4] as Step[]).map((s) => (
            <div
              key={s}
              className={`w-2 h-2 rounded-full transition-colors ${
                s === step ? 'bg-sky-500' : s < step ? 'bg-sky-800' : 'bg-gray-100 dark:bg-gray-700'
              }`}
            />
          ))}
        </div>

        <div className="bg-gray-50 dark:bg-gray-900 rounded-2xl p-8 border border-gray-200 dark:border-gray-800">
          {step === 1 && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Set up your organization</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Create your Arbie account. You'll receive an email to set your password.
              </p>
              <form onSubmit={handleRegister} className="space-y-4">
                <div>
                  <label htmlFor="orgName" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Organization name
                  </label>
                  <input
                    id="orgName"
                    type="text"
                    required
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Acme Corp"
                    className="w-full rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Admin email
                  </label>
                  <input
                    id="email"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="you@company.com"
                    className="w-full rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                {error && <p className="text-sm text-red-400">{error}</p>}
                <button
                  type="submit"
                  disabled={loading || !orgName.trim() || !email.trim()}
                  className="w-full rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-medium py-2 text-sm transition-colors"
                >
                  {loading ? 'Creating account…' : 'Continue'}
                </button>
              </form>
            </>
          )}

          {step === 2 && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Check your email</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                We sent a setup link to{' '}
                <span className="text-gray-900 dark:text-white font-medium">{email}</span>.
                Click the link to set your password, then come back here.
              </p>
              <button
                onClick={() => setStep(3)}
                className="w-full rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-medium py-2 text-sm transition-colors"
              >
                I&apos;ve set my password &mdash; continue
              </button>
            </>
          )}

          {step === 3 && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">Connect SmartRecruiters</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
                Enter your SmartRecruiters API key to enable candidate syncing and AI reviews.
                You can also do this later in Settings.
              </p>
              <div className="space-y-3">
                <div>
                  <label htmlFor="srApiKey" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    SmartRecruiters API key
                  </label>
                  <input
                    id="srApiKey"
                    type="password"
                    value={srApiKey}
                    onChange={(e) => { setSrApiKey(e.target.value); setSrTestStatus('idle'); }}
                    placeholder="sk-••••••••"
                    className="w-full rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-500"
                  />
                </div>
                {srTestStatus === 'valid' && (
                  <p className="text-sm text-green-400">✓ API key is valid</p>
                )}
                {srTestStatus === 'invalid' && (
                  <p className="text-sm text-red-400">{srTestError || 'API key is invalid'}</p>
                )}
                <button
                  type="button"
                  onClick={handleTestSrKey}
                  disabled={!srApiKey.trim() || srTestStatus === 'testing'}
                  className="w-full rounded-lg bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 disabled:opacity-50 text-gray-900 dark:text-white font-medium py-2 text-sm transition-colors"
                >
                  {srTestStatus === 'testing' ? 'Testing…' : 'Test connection'}
                </button>
                <button
                  type="button"
                  onClick={handleSaveSrKey}
                  disabled={srTestStatus !== 'valid' || loading}
                  className="w-full rounded-lg bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white font-medium py-2 text-sm transition-colors"
                >
                  {loading ? 'Saving…' : 'Save and continue'}
                </button>
                <button
                  type="button"
                  onClick={() => setStep(4)}
                  className="w-full rounded-lg text-gray-500 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white font-medium py-2 text-sm transition-colors"
                >
                  Skip for now
                </button>
              </div>
            </>
          )}

          {step === 4 && (
            <>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-white mb-1">You&apos;re all set!</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
                Your Arbie workspace is ready. Go to your dashboard to get started.
              </p>
              <button
                type="button"
                onClick={() => navigate('/dashboard', { replace: true })}
                className="block w-full rounded-lg bg-sky-600 hover:bg-sky-500 text-white font-medium py-2 text-sm text-center transition-colors"
              >
                Go to dashboard
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
